<script setup lang="ts">
import type { IntentAmount, PaymentIntentCreatePayload } from '~/composables/api/payment-intent/useFetchPaymentIntent'

const props = defineProps<{
  disabled?: boolean
  focusOnReady?: boolean
}>()

const { activeWorkspaceId } = useAuth()
const { getPaymentIntent: getPaymentIntentHandler } = usePaymentIntent()

const {
  initializeCardElement,
  uninitializeCardElement,
  getPaymentMethod,
  confirmCardSetup,
  cardInstance,
  isValid,
  isComplete,
} = useStripe({ focusOnReady: props.focusOnReady })

const { mutateAsync: createPaymentSource } = useMutationPaymentSource.create()

watchEffect(() => {
  cardInstance.value?.update({ disabled: props.disabled })
})

const element = ref<HTMLDivElement>()

onMounted(() => {
  if (!element.value)
    return

  initializeCardElement(element.value)
})

tryOnBeforeUnmount(uninitializeCardElement)

const { execute: saveAction, isLoading: isSaveActionLoading } = useAsyncState(async () => {
  if (!activeWorkspaceId.value)
    return

  const paymentMethod = await getPaymentMethod()
  const paymentIntent = await useApi().setupIntent().then(({ data }) => data)
  const res = await confirmCardSetup(paymentIntent.attributes.clientSecret, paymentMethod?.id)
  if (res?.error)
    throw res.error

  const paymentSource = await createPaymentSource({
    workspaceId: activeWorkspaceId.value,
    payload: { stripeIntentId: paymentIntent.id, replacePrimary: true },
  }).then(({ data }) => data)

  return paymentSource
}, undefined, { immediate: false, throwError: true })

const { execute: getPaymentIntent, isLoading: isPaymentIntentActionLoading } = useAsyncState(async ({ amount }: IntentAmount) => {
  const stripePaymentMethodId = (await getPaymentMethod())?.id

  if (!stripePaymentMethodId)
    return

  const intentData = {
    amount,
    stripePaymentMethodId,
  } satisfies PaymentIntentCreatePayload

  return await getPaymentIntentHandler(intentData)
}, undefined, { immediate: false, throwError: true })

const isLoading = computed(() => isPaymentIntentActionLoading.value || isSaveActionLoading.value)
const isLoadingDelayed = refDelayed(isLoading, false)

defineExpose({
  save: () => saveAction(0),
  getPaymentIntent: (args: IntentAmount) => getPaymentIntent(0, args),
  isValid,
  isComplete,
})
</script>

<template>
  <div
    class="relative min-h-11 rounded-lg border border-gray-300 px-3.5 py-[12.5px] text-gray-800"
  >
    <div
      v-show="!isLoadingDelayed"
      ref="element"
    />
    <div
      v-if="isLoadingDelayed"
      class="absolute inset-0 flex items-center px-3.5"
    >
      <DTLoaderDots />
    </div>
  </div>
</template>
